import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { LANG_SIGNAL } from '@lyricstream/shared';

@Component({
  selector: 'app-premium',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [],
  templateUrl: './premium.html',
  styleUrl: './premium.scss',
})
export class PremiumComponent {
  protected readonly lang = inject(LANG_SIGNAL);

  protected readonly billingPeriod = signal<'month' | 'year'>('month');

  protected readonly whyReasons = [
    {
      num: '— Raison n° 01',
      title: "Pour toi, l'écoute sans friction.",
      desc: "Téléchargements hors-ligne pour le service du dimanche dans une zone sans wifi, qualité studio FLAC, pas une seule pub avant un chant.",
      footnote: "Tes téléchargements restent les tiens, même si tu annules.",
    },
    {
      num: '— Raison n° 02',
      title: "Pour ton église, des outils sérieux.",
      desc: "Mode projection HD avec télécommande mobile, partitions au format PDF, mode multi-écran, et — sur le plan Paroisse — jusqu'à dix musiciens et chefs de choeur partagent le même compte.",
      footnote: "Licence collective claire pour ton vidéoprojecteur.",
    },
    {
      num: '— Raison n° 03',
      title: "Pour la communauté, un don continu.",
      desc: "Chaque abonnement finance les modérateurs, les traducteurs bénévoles et les serveurs qui hébergent plus de 12 000 chants en accès libre. Le gratuit ne disparaît jamais, grâce à toi.",
      footnote: "30% de chaque mois va à un fonds communautaire transparent.",
    },
  ];

  protected readonly testimonials = [
    {
      initials: 'EH',
      avatarBg: 'linear-gradient(135deg,#1B3A8C,#FF6A1F)',
      name: 'Église du Cap',
      role: 'Cap-Haïtien · 240 fidèles',
      text: "Notre vidéoprojecteur affiche enfin les paroles nettes, sans bug et sans pub. Le pasteur clique sur son téléphone, les paroles avancent. Avant on imprimait tout chaque dimanche.",
      plan: 'paroisse' as const,
    },
    {
      initials: 'MJ',
      avatarBg: 'linear-gradient(135deg,#103E8A,#7E2A0C)',
      name: 'Marc-Andy Joseph',
      role: 'Chef de choeur · Port-au-Prince',
      text: "Je dirige une chorale de 15 personnes. Avant, je gérais mes accords sur un tableur Excel partagé. Maintenant tout est dans LyricStream — les répétitions du jeudi soir sont divisées par deux.",
      plan: 'premium' as const,
    },
    {
      initials: 'DP',
      avatarBg: 'linear-gradient(135deg,#6B3FA0,#FF924F)',
      name: 'Danielle Pierre',
      role: 'Pianiste · Montréal',
      text: "Je suis musicienne nomade. Je pars souvent dans des zones sans réseau. Premium offline a changé ma vie — j'ai mes 800 chants partout avec moi.",
      plan: 'premium' as const,
    },
  ];

  protected readonly faqs = [
    {
      q: 'Puis-je essayer Premium gratuitement ?',
      a: "Oui — 14 jours, accès complet. On t'envoie un rappel 3 jours avant la fin pour que tu décides en toute conscience.",
    },
    {
      q: "Mes chants téléchargés restent-ils si j'annule ?",
      a: "Oui — tes téléchargements existants restent lisibles dans l'app, même hors-ligne. Tu ne peux simplement plus en ajouter de nouveaux, et la qualité repasse à 192 kbps pour le streaming.",
    },
    {
      q: 'Qui peut utiliser le plan Paroisse ?',
      a: "Toute église, chorale, communauté ou ministère organisé. Tu invites jusqu'à 10 personnes qui partagent l'abonnement. La licence projection est claire et couvre ton vidéoprojecteur.",
    },
    {
      q: 'Quels modes de paiement acceptez-vous ?',
      a: 'MonCash et Natcash sont disponibles directement. Sinon, carte Visa / Mastercard, Apple Pay, Google Pay, ou virement pour le plan Paroisse.',
    },
    {
      q: 'Est-ce que LyricStream restera gratuit ?',
      a: "Oui, c'est notre engagement fondateur. Les + 12 000 chants resteront accessibles gratuitement à tout le monde. Premium finance les serveurs et les équipes, mais ne ferme jamais la porte du gratuit.",
    },
    {
      q: 'Comment annuler ?',
      a: "En deux clics, depuis tes Paramètres → Abonnement. Pas de mail à envoyer, pas de question piège. L'accès Premium reste actif jusqu'à la fin de la période en cours.",
    },
    {
      q: 'Vous êtes étudiant, pasteur ou en formation ?',
      a: "Premium est offert gratuitement pendant 12 mois pour les étudiants en théologie ou en musique sacrée, et les pasteurs en stage. Envoie-nous une preuve d'inscription et c'est activé sous 48 h.",
    },
  ];

  protected readonly openFaq = signal<number | null>(0);

  toggleFaq(index: number): void {
    this.openFaq.update(cur => cur === index ? null : index);
  }
}
